import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import { RedemptionProvider as RedemptionProviderType } from 'models/RedemptionProvider'
import { getInStoreValue } from 'scenes/RedeemManager/helpers'
import { InstoreRedemptionType } from 'scenes/RedeemManager/types'
import { CouponPoolDisplayType } from '../helpers'
import CouponCodesDisplay from './CouponCodesDisplay'

type ProviderInstoreRedemptionDisplayProps = {
  redemptionProvider: RedemptionProviderType
  redemptionType: InstoreRedemptionType
  template?: RedeemTemplate
  couponCodeDisplay: CouponPoolDisplayType
  instoreDiscountedProviderProductIds?: string[]
  instoreRequiredProviderProductIds?: string[]
  instoreDiscountedProviderModifierIds?: string[]
  instoreProviderModifierDiscountIds?: string[]
  editButton?: () => void
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.instore_redemption_card'
)

const ProviderInstoreRedemptionDisplay = (
  props: ProviderInstoreRedemptionDisplayProps
) => {
  const {
    redemptionProvider,
    redemptionType,
    template,
    couponCodeDisplay,
    instoreDiscountedProviderProductIds,
    instoreRequiredProviderProductIds,
    instoreDiscountedProviderModifierIds,
    instoreProviderModifierDiscountIds,
    editButton,
  } = props

  const isModifier = template?.subtype === 'modifier'
  const { couponCode, formattedCouponPools } = couponCodeDisplay
  const inStoreValues =
    template?.product_ids?.[redemptionProvider.value]?.instore

  const requiredIdsForPos = getInStoreValue(
    inStoreValues,
    instoreRequiredProviderProductIds
  )

  // For modifier templates: products use 'discounted_related', modifiers use 'discounted'
  // For item templates: products use 'discounted', modifiers use 'discounted_related'
  const getDiscountedIds = (
    ids: string[] | undefined,
    isForModifier: boolean
  ) => {
    const key =
      isModifier === isForModifier ? 'discounted' : 'discounted_related'
    return getInStoreValue(inStoreValues, ids, key)
  }

  const discountedIdsForPos = getDiscountedIds(
    instoreDiscountedProviderProductIds,
    false
  )
  const discountedModifierIdsForPos = getDiscountedIds(
    instoreDiscountedProviderModifierIds,
    true
  )

  const modifierDiscountIdsForPos = getInStoreValue(
    inStoreValues,
    instoreProviderModifierDiscountIds,
    'discounted_modifiers'
  )

  const discountedProductsLabel = isModifier
    ? t('multi_pos.discounted_modifier_item_ids_for_pos')
    : t('multi_pos.discounted_ids_for_pos')
  const discountedModifiersLabel = isModifier
    ? t('multi_pos.discounted_modifier_ids_for_pos')
    : t('multi_pos.discounted_item_modifier_ids_for_pos')

  return (
    <div
      className="d-flex flex-column p-4 mb-4"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '4px',
        border: '1px solid #D6DADE',
      }}
    >
      <div className="d-flex align-items-start justify-content-between mb-4">
        <div className="d-flex flex-column">
          <Text.BodyText3 bold>{t('multi_pos.pos')}</Text.BodyText3>
          <Text.BodyText3 color="grey70">
            {redemptionProvider.label}
          </Text.BodyText3>
        </div>

        {!!editButton && editButton()}
      </div>

      <div className="d-flex flex-column">
        <Text.BodyText3 bold>{t('multi_pos.configuration')}</Text.BodyText3>
        {redemptionType === InstoreRedemptionType.MANAGER_COMP && (
          <>
            <Text.BodyText3 color="grey70">
              {t('type.manager_comp')}
            </Text.BodyText3>
            <Text.BodyText3 color="grey70">
              {t('type.manager_comp_info')}
            </Text.BodyText3>
          </>
        )}
        {redemptionType === InstoreRedemptionType.COUPON_CODES && (
          <>
            <CouponCodesDisplay
              redemptionProvider={redemptionProvider}
              couponCodes={couponCode}
              selectedCouponPools={formattedCouponPools}
            />
            <Switch condition={Boolean(discountedIdsForPos.length)}>
              <Text.BodyText3 className="mt-s" bold>
                {discountedProductsLabel}
              </Text.BodyText3>
              <Text.BodyText3 color="grey70">
                {discountedIdsForPos.join(',')}
              </Text.BodyText3>
            </Switch>

            <Switch condition={Boolean(discountedModifierIdsForPos.length)}>
              <Text.BodyText3 className="mt-s" bold>
                {discountedModifiersLabel}
              </Text.BodyText3>
              <Text.BodyText3 color="grey70">
                {discountedModifierIdsForPos.join(',')}
              </Text.BodyText3>
            </Switch>

            <Switch condition={Boolean(modifierDiscountIdsForPos.length)}>
              <Text.BodyText3 className="mt-s" bold>
                {t('multi_pos.modifier_discount_ids_for_pos')}
              </Text.BodyText3>
              <Text.BodyText3 color="grey70">
                {modifierDiscountIdsForPos.join(',')}
              </Text.BodyText3>
            </Switch>

            <Switch condition={Boolean(requiredIdsForPos.length)}>
              <Text.BodyText3 className="mt-s" bold>
                {t('multi_pos.required_ids_for_pos')}
              </Text.BodyText3>
              <Text.BodyText3 color="grey70">
                {requiredIdsForPos.join(',')}
              </Text.BodyText3>
            </Switch>

            <Text.BodyText3 className="mb-4 mt-l" color="grey70">
              <Switch condition={isModifier}>
                {t('multi_pos.discounted_modifier_text')}
              </Switch>
              <Switch condition={!isModifier}>
                {t('multi_pos.discounted_item_modifier_text')}
              </Switch>
            </Text.BodyText3>
          </>
        )}
        {redemptionType === InstoreRedemptionType.CASH_BACK && (
          <>
            <Text.BodyText3 className="mb-4" color="grey70">
              {t('type.automatic_cash_back')}
            </Text.BodyText3>
          </>
        )}
      </div>
    </div>
  )
}

export default ProviderInstoreRedemptionDisplay
